import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faAward,
  faCheckCircle,
  faBuildingColumns,
  faCar,
  faBriefcase,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { experience, recognitions, cveAchievement } from "../data/portfolioData";

export default function Work() {
  const currentRole = experience[0];

  const getRecognitionIcon = (title) => {
    if (title.includes("Ferrari")) return faCar;
    if (title.includes("Health") || title.includes("US Dept")) return faBuildingColumns;
    return faShieldHalved;
  };

  return (
    <section id="experience" className="cyber-section">
      <div className="container">
        {/* Section Header */}
        <div className="mb-5">
          <div className="section-label">PRACTICAL TRACK RECORD</div>
          <h2 className="section-title">Experience &amp; Responsible Disclosure</h2>
          <p className="section-subtitle">
            Hands-on technical security responsibilities across SIEM architecture, vulnerability
            assessment, and formally acknowledged vulnerability disclosures.
          </p>
        </div>

        <div className="row g-5">
          {/* Left Column: Vertical Experience Timeline */}
          <div className="col-lg-7">
            <div className="d-flex align-items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faBriefcase} style={{ color: "var(--accent)" }} />
              <h3 style={{ fontSize: "1.3rem", fontWeight: 700, margin: 0 }}>
                Security Operations &amp; Analysis
              </h3>
            </div>

            <div
              className="position-relative ps-4"
              style={{
                borderLeft: "2px solid var(--border-subtle)",
                marginLeft: "8px",
              }}
            >
              <div
                className="cyber-card p-4 mb-4"
                style={{
                  background: "var(--bg-surface)",
                  position: "relative",
                }}
              >
                {/* Dot marker on line */}
                <div
                  style={{
                    position: "absolute",
                    left: "-33px",
                    top: "24px",
                    width: "14px",
                    height: "14px",
                    borderRadius: "50%",
                    backgroundColor: "var(--accent)",
                    border: "3px solid #080A0D",
                    boxShadow: "0 0 10px var(--accent)",
                  }}
                />

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <h4 style={{ fontSize: "1.25rem", fontWeight: 700, margin: 0, color: "var(--text-primary)" }}>
                    {currentRole.role}
                  </h4>
                  <span className="status-pill" style={{ fontSize: "0.75rem", padding: "3px 10px" }}>
                    Currently Working
                  </span>
                </div>

                <p style={{ color: "var(--text-secondary)", fontSize: "0.92rem", marginBottom: "20px" }}>
                  {currentRole.summary}
                </p>

                <div className="font-mono text-muted text-uppercase mb-3" style={{ fontSize: "0.75rem", letterSpacing: "0.08em" }}>
                  Key Responsibilities &amp; Implementations
                </div>

                <div className="d-flex flex-column gap-3">
                  {currentRole.responsibilities.map((resp, idx) => (
                    <div key={idx} className="d-flex align-items-start gap-3">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        style={{ color: "var(--accent)", marginTop: "4px", fontSize: "0.85rem", flexShrink: 0 }}
                      />
                      <span style={{ fontSize: "0.9rem", color: "var(--text-primary)", lineHeight: 1.6 }}>
                        {resp}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Proof of Work & Recognitions */}
          <div className="col-lg-5">
            <div className="d-flex align-items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faAward} style={{ color: "var(--warning)" }} />
              <h3 style={{ fontSize: "1.3rem", fontWeight: 700, margin: 0 }}>
                Proof of Work &amp; Hall of Fame
              </h3>
            </div>

            <p style={{ fontSize: "0.92rem", color: "var(--text-secondary)", marginBottom: "24px" }}>
              Formal recognitions from government security programs and global enterprises for
              identifying and responsibly disclosing security vulnerabilities.
            </p>

            {/* Featured CVE Security Achievement */}
            <div
              className="cyber-card p-4 mb-4"
              style={{
                background: "linear-gradient(135deg, rgba(14, 17, 22, 0.95) 0%, rgba(21, 25, 34, 0.95) 100%)",
                border: "1px solid rgba(56, 189, 248, 0.35)",
                boxShadow: "0 10px 25px -10px rgba(0, 0, 0, 0.7), 0 0 20px -5px var(--accent-glow)",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="section-label mb-0" style={{ fontSize: "0.68rem", padding: "2px 8px" }}>
                  {cveAchievement.label}
                </span>
                <span
                  className="badge font-mono"
                  style={{
                    background: "rgba(56, 189, 248, 0.1)",
                    color: "var(--accent)",
                    border: "1px solid rgba(56, 189, 248, 0.25)",
                    fontSize: "0.7rem",
                  }}
                >
                  Published CVE Record
                </span>
              </div>

              <h4
                className="font-mono mt-2 mb-1"
                style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--accent)" }}
              >
                {cveAchievement.cveId}
              </h4>

              <div
                style={{
                  fontWeight: 600,
                  fontSize: "0.92rem",
                  color: "var(--text-primary)",
                  marginBottom: "4px",
                }}
              >
                {cveAchievement.role}
              </div>

              <div
                className="font-mono text-muted mb-3"
                style={{ fontSize: "0.78rem" }}
              >
                {cveAchievement.title}
              </div>

              <p
                style={{
                  fontSize: "0.85rem",
                  color: "var(--text-secondary)",
                  lineHeight: 1.6,
                  marginBottom: "16px",
                }}
              >
                {cveAchievement.description}
              </p>

              <a
                href={cveAchievement.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cyber-primary"
                style={{ fontSize: "0.82rem", padding: "8px 16px", display: "inline-block", textDecoration: "none" }}
              >
                View CVE Record →
              </a>
            </div>

            <div className="d-flex flex-column gap-3">
              {recognitions.map((item, idx) => {
                const CardWrapper = item.url ? "a" : "div";
                return (
                  <CardWrapper
                    key={idx}
                    href={item.url || undefined}
                    target={item.url ? "_blank" : undefined}
                    rel={item.url ? "noopener noreferrer" : undefined}
                    className="cyber-card p-3 text-decoration-none d-block"
                    style={{
                      background: "var(--bg-surface)",
                      borderColor: "var(--border-subtle)",
                      color: "inherit",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: "10px",
                            background: `${item.badgeColor}15`,
                            border: `1px solid ${item.badgeColor}30`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: item.badgeColor,
                            fontSize: "1.1rem",
                            flexShrink: 0,
                          }}
                        >
                          <FontAwesomeIcon icon={getRecognitionIcon(item.title)} />
                        </div>
                        <div>
                          <div
                            className="d-flex align-items-center gap-2"
                            style={{ fontWeight: 700, fontSize: "1rem", color: "var(--text-primary)" }}
                          >
                            <span>{item.title}</span>
                            {item.url && (
                              <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                style={{ fontSize: "0.75rem", color: "var(--accent)" }}
                              />
                            )}
                          </div>
                          <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                            {item.organization}
                          </div>
                          <div className="font-mono text-muted" style={{ fontSize: "0.72rem" }}>
                            {item.detail}
                          </div>
                        </div>
                      </div>

                      <div className="text-end">
                        <span
                          className="font-mono"
                          style={{
                            display: "inline-block",
                            fontSize: "1.2rem",
                            fontWeight: 700,
                            color: item.badgeColor,
                            background: `${item.badgeColor}15`,
                            padding: "4px 14px",
                            borderRadius: "9999px",
                            border: `1px solid ${item.badgeColor}40`,
                          }}
                        >
                          {item.count}
                        </span>
                        <div className="font-mono text-muted mt-1" style={{ fontSize: "0.68rem" }}>
                          {item.type}
                        </div>
                      </div>
                    </div>
                  </CardWrapper>
                );
              })}
            </div>

            {/* Disclosure Ethics Notice */}
            <div
              className="p-3 rounded-2 mt-4"
              style={{
                background: "rgba(255, 255, 255, 0.02)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-1" style={{ color: "var(--accent)" }}>
                <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: "0.85rem" }} />
                <span className="font-mono" style={{ fontSize: "0.78rem", fontWeight: 600 }}>
                  RESPONSIBLE DISCLOSURE ETHICS
                </span>
              </div>
              <p className="mb-0" style={{ fontSize: "0.8rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                All security disclosures adhere strictly to established Responsible Vulnerability Disclosure
                Programs (RVDP), respecting coordinated disclosure timelines and customer privacy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
